import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../../stores';
import { useApi } from '../../hooks/useApi';
import { Loader } from './Loader';
import { canAccessSubscriptionGatedFeatures } from '../../utils/subscriptionAccess';

interface SubscriptionGatedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * Blocks followers without an active subscription from gated routes.
 * Masters and admins always pass through.
 */
const SubscriptionGatedRoute: React.FC<SubscriptionGatedRouteProps> = ({
  children,
  redirectTo = '/dashboard/pricing',
}) => {
  const { user } = useAuthStore();
  const isFollower = user?.role === 'follower';
  const { data: subscriptionData, isLoading } = useApi.useSubscriptionStatus({
    enabled: isFollower,
  });

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'master_pending') {
    return <Navigate to="/register/pending" replace />;
  }

  if (isFollower && isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader fullScreen={false} />
      </div>
    );
  }

  const canAccess = canAccessSubscriptionGatedFeatures(user, subscriptionData);
  if (!canAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

export default SubscriptionGatedRoute;
